/**
 * Family-setup wizard — the NORMAL-family onboarding flow (FAMILY-PIVOT-UX §5).
 *
 * Runs AFTER ProfileSetupScreen and BEFORE the AppShell, replacing the bare
 * single-step OnboardingScreen. Its job is to land a brand-new user on a
 * NON-EMPTY Home in ~60 seconds:
 *
 *   1. Create family            → POST /families  (server defaults mode='family')
 *   2. Add kids                 → reuse AddChildDialog inputs (name + emoji + DOB)
 *   3. Invite partner by email  → role 'parent' (full access, no scope picker)
 *   4. (opt) Invite grandparent → role 'trusted' + scope checklist
 *   5. Seed a first shopping list → prefilled "Potraviny" / "Drogéria"
 *
 * Every step is skippable; the family (step 1) is the only hard requirement —
 * once it exists, `onCreated()` reloads `me()` so the AppShell can mount on a
 * populated Home.
 *
 * This screen runs OUTSIDE <FamilyProvider> (there is no active family yet), so
 * it cannot reuse the SharingModule/AddChildDialog dialogs verbatim — those read
 * `useFamily()`. Instead it talks to the API directly via `useApi()` and mirrors
 * the exact same @solandia/core primitives (createChildInputSchema,
 * inviteMemberInputSchema, normalizeScopes, ALL_SCOPES, isSensitiveScope) and the
 * same `scope-toggle` / `role-option` markup, so behaviour stays identical.
 *
 * NOTE: wizard-specific copy below uses inline Slovak literals where no matching
 * key exists in the i18n dictionary yet. These are flagged for extraction into a
 * dedicated `t.onboarding.*` group (see the PR follow-ups) — do not hand-edit
 * sk.ts from here.
 */
import { useState, type ReactNode } from 'react';
import {
  ALL_SCOPES,
  createChildInputSchema,
  inviteMemberInputSchema,
  isSensitiveScope,
  normalizeScopes,
  type Id,
  type Scope,
} from '@solandia/core';
import { useI18n } from '../i18n';
import { useApi } from '../api/ApiProvider';
import { Wordmark } from '../components/Logo';
import { ErrorState, Field, Form } from '../components/ui';

/** Same emoji palette the AddChildDialog offers, kept in sync deliberately. */
const EMOJI_CHOICES = ['🐥', '🦊', '🐰', '🐼', '🐨', '🦄', '🐯', '🐸', '⭐', '🌈'] as const;

/** Default shopping list suggestions seeded at step 5 (the daily-habit hook). */
const SEED_LIST_SUGGESTIONS = ['Potraviny', 'Drogéria'] as const;

/** Scopes pre-checked when inviting a grandparent (sensible, non-sensitive). */
const DEFAULT_TRUSTED_SCOPES: readonly Scope[] = ['calendar', 'contacts', 'children'];

const STEP_COUNT = 5;

export function FamilySetupWizard({ onDone }: { onDone: () => void }) {
  const { t } = useI18n();
  const api = useApi();

  // 1-based step index. Step 1 creates the family; the rest need its id.
  const [step, setStep] = useState(1);
  const [familyId, setFamilyId] = useState<Id | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 1 — family name.
  const [familyName, setFamilyName] = useState(t.family.namePlaceholder);

  // Step 2 — child being added (one at a time; "add another" loops the step).
  const [childName, setChildName] = useState('');
  const [childEmoji, setChildEmoji] = useState<string>(EMOJI_CHOICES[0]);
  const [childBirthDate, setChildBirthDate] = useState('');
  const [childrenAdded, setChildrenAdded] = useState(0);

  // Step 3 — partner email (role 'parent').
  const [partnerEmail, setPartnerEmail] = useState('');

  // Step 4 — grandparent email (role 'trusted') + scope checklist.
  const [grandparentEmail, setGrandparentEmail] = useState('');
  const [grandparentScopes, setGrandparentScopes] = useState<Set<Scope>>(
    () => new Set<Scope>(DEFAULT_TRUSTED_SCOPES),
  );

  // Step 5 — first shopping list.
  const [listName, setListName] = useState<string>(SEED_LIST_SUGGESTIONS[0]);

  function next() {
    setError(null);
    if (step >= STEP_COUNT) {
      onDone();
      return;
    }
    setStep((s) => s + 1);
  }

  function toggleScope(scope: Scope, on: boolean) {
    setGrandparentScopes((prev) => {
      const nextSet = new Set(prev);
      if (on) nextSet.add(scope);
      else nextSet.delete(scope);
      return nextSet;
    });
  }

  /** Step 1: create the family, then advance. Hard requirement to continue. */
  async function createFamily() {
    const trimmed = familyName.trim();
    if (!trimmed) {
      setError(t.common.required);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const family = await api.createFamily({ name: trimmed });
      setFamilyId(family.id);
      setBusy(false);
      next();
    } catch {
      setError(t.errors.generic);
      setBusy(false);
    }
  }

  /** Step 2: add the current child, then reset the inputs to add another. */
  async function addChild() {
    if (!familyId) return;
    const parsed = createChildInputSchema.safeParse({
      name: childName.trim(),
      avatarEmoji: childEmoji,
      birthDate: childBirthDate,
    });
    if (!parsed.success) {
      setError(t.common.required);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.createChild(familyId, parsed.data);
      setChildrenAdded((n) => n + 1);
      // Reset for the next child; stay on the step so adding several is one tap.
      setChildName('');
      setChildEmoji(EMOJI_CHOICES[0]);
      setChildBirthDate('');
      setBusy(false);
    } catch {
      setError(t.errors.generic);
      setBusy(false);
    }
  }

  /** Step 3: invite the partner as a full-access parent. */
  async function invitePartner() {
    if (!familyId) return;
    const parsed = inviteMemberInputSchema.safeParse({
      email: partnerEmail.trim(),
      role: 'parent',
      // Parents always get full access; the server normalises permissions.
      permissions: undefined,
    });
    if (!parsed.success) {
      setError(t.common.required);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.inviteMember(familyId, parsed.data);
      setBusy(false);
      next();
    } catch {
      setError(t.errors.generic);
      setBusy(false);
    }
  }

  /** Step 4: invite a grandparent as a scoped trusted member. */
  async function inviteGrandparent() {
    if (!familyId) return;
    const permissions = normalizeScopes('trusted', [...grandparentScopes]);
    const parsed = inviteMemberInputSchema.safeParse({
      email: grandparentEmail.trim(),
      role: 'trusted',
      permissions,
    });
    if (!parsed.success) {
      setError(t.common.required);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.inviteMember(familyId, parsed.data);
      setBusy(false);
      next();
    } catch {
      setError(t.errors.generic);
      setBusy(false);
    }
  }

  /** Step 5: seed the first shopping list, then finish into Home. */
  async function seedList() {
    if (!familyId) return;
    const trimmed = listName.trim();
    if (!trimmed) {
      setError(t.common.required);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.createShoppingList(familyId, trimmed);
      setBusy(false);
      onDone();
    } catch {
      setError(t.errors.generic);
      setBusy(false);
    }
  }

  return (
    <main className="auth-screen">
      <div className="card auth-card" style={{ textAlign: 'left' }}>
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <Wordmark size={32} />
        </div>

        {/* Progress indicator — "Krok N z 5". */}
        <p className="text-xs muted" style={{ textAlign: 'center', marginBottom: 4 }}>
          {t.tour.progress
            .replace('{step}', String(step))
            .replace('{steps}', String(STEP_COUNT))}
        </p>
        <WizardProgress step={step} total={STEP_COUNT} />

        {error ? (
          <div style={{ marginBottom: 12 }}>
            <ErrorState message={error} />
          </div>
        ) : null}

        {step === 1 ? (
          <Step
            title={t.family.createTitle}
            lead={t.family.createLead}
            // Step 1 is the only non-skippable step: a family must exist.
            onSubmit={createFamily}
            submitLabel={busy ? t.common.saving : t.family.create}
            busy={busy}
          >
            <Field label={t.family.nameLabel}>
              {({ id, invalid }) => (
                <input
                  id={id}
                  className="input"
                  value={familyName}
                  aria-invalid={invalid}
                  onChange={(e) => setFamilyName(e.target.value)}
                  placeholder={t.family.namePlaceholder}
                  autoFocus
                />
              )}
            </Field>
          </Step>
        ) : null}

        {step === 2 ? (
          <Step
            title={t.children.addTitle}
            lead={t.children.noneLead}
            onSubmit={addChild}
            submitLabel={busy ? t.common.saving : t.children.add}
            busy={busy}
            onSkip={next}
            skipLabel={childrenAdded > 0 ? 'Pokračovať' : 'Preskočiť'}
            extraNote={
              childrenAdded > 0
                ? `Pridané deti: ${childrenAdded}. Pridajte ďalšie alebo pokračujte.`
                : undefined
            }
          >
            <Field label={t.children.nameLabel}>
              {({ id, invalid }) => (
                <input
                  id={id}
                  className="input"
                  value={childName}
                  aria-invalid={invalid}
                  onChange={(e) => setChildName(e.target.value)}
                  placeholder={t.children.namePlaceholder}
                  autoFocus
                />
              )}
            </Field>

            <Field label={t.children.emojiLabel}>
              {() => (
                <div
                  className="row wrap"
                  role="radiogroup"
                  aria-label={t.children.emojiLabel}
                >
                  {EMOJI_CHOICES.map((e) => (
                    <button
                      key={e}
                      type="button"
                      className="pill"
                      aria-pressed={childEmoji === e}
                      onClick={() => setChildEmoji(e)}
                      style={{ fontSize: '1.3rem', padding: '6px 12px' }}
                    >
                      {e}
                    </button>
                  ))}
                </div>
              )}
            </Field>

            <Field label={t.children.birthDateLabel}>
              {({ id, invalid }) => (
                <input
                  id={id}
                  type="date"
                  className="input"
                  value={childBirthDate}
                  aria-invalid={invalid}
                  onChange={(e) => setChildBirthDate(e.target.value)}
                />
              )}
            </Field>
          </Step>
        ) : null}

        {step === 3 ? (
          <Step
            title="Pozvite partnera"
            lead="Pozvite druhého rodiča — uvidí všetko a môže spravovať rodinu spolu s vami."
            onSubmit={invitePartner}
            submitLabel={busy ? t.common.saving : t.sharing.invite}
            busy={busy}
            onSkip={next}
            skipLabel="Preskočiť"
          >
            <Field label={t.sharing.emailLabel}>
              {({ id, invalid }) => (
                <input
                  id={id}
                  type="email"
                  className="input"
                  value={partnerEmail}
                  aria-invalid={invalid}
                  onChange={(e) => setPartnerEmail(e.target.value)}
                  placeholder="meno@email.sk"
                  autoFocus
                />
              )}
            </Field>
            <p className="banner banner-info" role="note">
              <span aria-hidden="true">ℹ️</span>
              <span className="grow">{t.sharing.parentScopesNote}</span>
            </p>
          </Step>
        ) : null}

        {step === 4 ? (
          <Step
            title="Pozvite babku či dedka"
            lead={t.sharing.roleTrustedDesc}
            onSubmit={inviteGrandparent}
            submitLabel={busy ? t.common.saving : t.sharing.invite}
            busy={busy}
            onSkip={next}
            skipLabel="Preskočiť"
          >
            <Field label={t.sharing.emailLabel}>
              {({ id, invalid }) => (
                <input
                  id={id}
                  type="email"
                  className="input"
                  value={grandparentEmail}
                  aria-invalid={invalid}
                  onChange={(e) => setGrandparentEmail(e.target.value)}
                  placeholder="meno@email.sk"
                  autoFocus
                />
              )}
            </Field>

            <div className="field">
              <span className="field-label">{t.sharing.scopesLabel}</span>
              <span className="field-hint">{t.sharing.scopesHint}</span>
              <div className="scope-list" style={{ marginTop: 8 }}>
                {ALL_SCOPES.map((s) => {
                  const sensitive = isSensitiveScope(s);
                  return (
                    <label
                      key={s}
                      className={sensitive ? 'scope-toggle sensitive' : 'scope-toggle'}
                    >
                      <input
                        type="checkbox"
                        checked={grandparentScopes.has(s)}
                        onChange={(e) => toggleScope(s, e.target.checked)}
                      />
                      <span className="grow">
                        {t.scopes[s]}
                        {sensitive ? (
                          <span className="text-xs muted" style={{ display: 'block' }}>
                            {t.sharing.sensitiveNote}
                          </span>
                        ) : null}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          </Step>
        ) : null}

        {step === 5 ? (
          <Step
            title="Prvý nákupný zoznam"
            lead={t.shopping.noListsLead}
            onSubmit={seedList}
            submitLabel={busy ? t.common.saving : t.common.add}
            busy={busy}
            onSkip={onDone}
            skipLabel="Preskočiť a dokončiť"
          >
            <Field label={t.shopping.listNameLabel}>
              {({ id, invalid }) => (
                <input
                  id={id}
                  className="input"
                  value={listName}
                  aria-invalid={invalid}
                  onChange={(e) => setListName(e.target.value)}
                  placeholder={t.shopping.listNamePlaceholder}
                  autoFocus
                />
              )}
            </Field>
            <div className="row wrap" role="group" aria-label={t.shopping.listNameLabel}>
              {SEED_LIST_SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  className="pill"
                  aria-pressed={listName === s}
                  onClick={() => setListName(s)}
                >
                  {s}
                </button>
              ))}
            </div>
          </Step>
        ) : null}
      </div>
    </main>
  );
}

/**
 * One wizard step: heading + lead, the step's form fields, a primary action and
 * an optional skip. Keeps every step visually consistent and accessible (the
 * form owns submit-on-enter; skip is a separate ghost button).
 */
function Step({
  title,
  lead,
  children,
  onSubmit,
  submitLabel,
  busy,
  onSkip,
  skipLabel,
  extraNote,
}: {
  title: string;
  lead: string;
  children: ReactNode;
  onSubmit: () => void;
  submitLabel: string;
  busy: boolean;
  onSkip?: () => void;
  skipLabel?: string;
  extraNote?: string;
}) {
  return (
    <>
      <h1 style={{ fontSize: '1.35rem' }}>{title}</h1>
      <p className="lead" style={{ margin: '6px 0 20px' }}>
        {lead}
      </p>
      <Form onSubmit={onSubmit}>
        {children}
        {extraNote ? (
          <p className="text-sm muted" style={{ margin: 0 }}>
            {extraNote}
          </p>
        ) : null}
        <button
          type="submit"
          className="btn btn-primary btn-block"
          disabled={busy}
        >
          {submitLabel}
        </button>
        {onSkip ? (
          <button
            type="button"
            className="btn btn-ghost btn-block"
            onClick={onSkip}
            disabled={busy}
          >
            {skipLabel}
          </button>
        ) : null}
      </Form>
    </>
  );
}

/** Minimal step dots — pure presentation, communicates wizard length + position. */
function WizardProgress({ step, total }: { step: number; total: number }) {
  return (
    <div
      className="row"
      style={{ justifyContent: 'center', gap: 6, marginBottom: 16 }}
      aria-hidden="true"
    >
      {Array.from({ length: total }, (_, i) => i + 1).map((n) => (
        <span
          key={n}
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background:
              n <= step ? 'var(--sol-indigo, #4F6DF5)' : 'var(--sol-border, #E3DED4)',
            transition: 'background 0.2s ease',
          }}
        />
      ))}
    </div>
  );
}
